import React from 'react';
import { useParams, Redirect } from 'react-router-dom';

import './styles.less';
import { colors, fonts } from '../Editorial/theme';
import logoWhite from 'assets/landing/lyfe-logo-white.png';
import Seo from 'components/Seo';
import { guides } from './data';
import StarRating from './StarRating';

// Meta descriptions cap ~160 chars; trim the guide intro on a word boundary.
function metaDescription(text) {
  if (text.length <= 160) return text;
  return `${text.slice(0, 157).replace(/\s+\S*$/, '')}…`;
}

const PinIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#75757d"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flex: 'none', marginTop: 1 }}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const SparkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={colors.orange} strokeWidth="1.8" strokeLinejoin="round">
    <polygon points="12 2 15 9 22 9.5 17 14.5 18.5 22 12 18 5.5 22 7 14.5 2 9.5 9 9" />
  </svg>
);

function VenueCard({ v }) {
  return (
    <div
      className="g-card"
      style={{ border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, background: colors.bgAlt, padding: '30px 32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
        <span style={{ fontSize: 13, letterSpacing: '.04em', color: colors.muted3 }}>{v.cats.join('  ·  ')}</span>
        {v.happyHour && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 12,
              color: colors.orange,
              border: '1px solid rgba(232,99,43,.45)',
              background: 'rgba(232,99,43,.08)',
              padding: '5px 12px',
              borderRadius: 999,
            }}>
            <SparkIcon /> Happy Hour
          </span>
        )}
      </div>
      <h3
        style={{
          fontFamily: fonts.serif,
          fontWeight: 400,
          fontSize: 'clamp(28px,3.4vw,38px)',
          lineHeight: 1.05,
          letterSpacing: '-.01em',
        }}>
        {v.name}
      </h3>
      <StarRating rating={v.rating} />
      <p style={{ fontSize: 16, lineHeight: 1.62, color: colors.muted, maxWidth: 720, textWrap: 'pretty' }}>{v.desc}</p>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 22, color: colors.muted4, fontSize: 14 }}>
        <PinIcon />
        <span>{v.address}</span>
      </div>
    </div>
  );
}

export default function Guide() {
  const { slug } = useParams();
  const guide = guides[slug];

  // Unknown slug → fall back to the first guide (mirrors the design's default).
  if (!guide) return <Redirect to="/guides/brickell" />;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '100vh',
        overflowX: 'hidden',
        background: colors.bg,
        color: colors.ink,
        fontFamily: fonts.sans,
      }}>
      <Seo
        title={`${guide.title} · Lyfe`}
        description={metaDescription(guide.intro)}
        path={`/guides/${slug}`}
        image={guide.cover}
      />
      {/* TOP BAR */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          background: 'rgba(10,10,12,.78)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(255,255,255,.07)',
        }}>
        <div
          style={{
            maxWidth: 980,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 24px',
          }}>
          <a
            className="g-back"
            href="/#guides"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 9, color: '#B9B9C0', textDecoration: 'none', fontSize: 14 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            All city guides
          </a>
          <img src={logoWhite} alt="Lyfe" style={{ height: 20, display: 'block', opacity: 0.92 }} />
        </div>
      </div>

      {/* COVER */}
      <div style={{ position: 'relative', width: '100%', height: 'clamp(340px,46vh,520px)', overflow: 'hidden' }}>
        <img src={guide.cover} alt={guide.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top,#0A0A0C 2%,rgba(10,10,12,.45) 42%,rgba(10,10,12,.15) 100%)',
          }}
        />
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
          <div style={{ maxWidth: 980, margin: '0 auto', padding: '0 24px 40px' }}>
            <span
              style={{
                display: 'inline-block',
                fontSize: 11,
                letterSpacing: '.16em',
                textTransform: 'uppercase',
                color: colors.ink,
                background: 'rgba(10,10,12,.6)',
                border: '1px solid rgba(255,255,255,.18)',
                backdropFilter: 'blur(6px)',
                padding: '7px 15px',
                borderRadius: 999,
                marginBottom: 20,
              }}>
              Guide
            </span>
            <h1
              style={{
                fontFamily: fonts.serif,
                fontWeight: 300,
                fontSize: 'clamp(40px,6.4vw,76px)',
                lineHeight: 1.0,
                letterSpacing: '-.015em',
              }}>
              {guide.title}
            </h1>
            <div
              style={{
                fontFamily: fonts.serif,
                fontStyle: 'italic',
                fontWeight: 300,
                fontSize: 'clamp(19px,2.4vw,26px)',
                color: colors.muted,
                marginTop: 12,
              }}>
              {guide.subtitle}
            </div>
          </div>
        </div>
      </div>

      {/* INTRO + LIST */}
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '48px 24px 120px' }}>
        <p
          style={{
            fontSize: 'clamp(17px,1.9vw,21px)',
            lineHeight: 1.62,
            color: '#D4D4DA',
            maxWidth: 760,
            marginBottom: 14,
            textWrap: 'pretty',
          }}>
          {guide.intro}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '34px 0 40px' }}>
          <span style={{ fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase', color: colors.muted2, fontWeight: 600 }}>
            {guide.venues.length} spots
          </span>
          <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,.1)' }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {guide.venues.map((v) => (
            <VenueCard key={v.name} v={v} />
          ))}
        </div>

        <div style={{ marginTop: 64, borderTop: '1px solid rgba(255,255,255,.08)', paddingTop: 48, textAlign: 'center' }}>
          <div style={{ fontFamily: fonts.serif, fontWeight: 300, fontSize: 'clamp(26px,3.4vw,38px)', lineHeight: 1.1 }}>
            Get the full map in the app.
          </div>
          <p style={{ fontSize: 16, color: colors.muted2, margin: '14px auto 28px', maxWidth: 420 }}>
            Live deals, real crowds, and every spot on this list — in your pocket.
          </p>
          <a
            href="/#get"
            style={{
              display: 'inline-block',
              background: colors.orange,
              color: colors.ink,
              fontWeight: 600,
              fontSize: 15,
              padding: '15px 32px',
              borderRadius: 999,
              textDecoration: 'none',
            }}>
            Get the app
          </a>
        </div>
      </div>
    </div>
  );
}
